import React, { useState, useRef } from "react";
import "../styles/Drones.css";
import { exportToCSV } from "../utils/exportUtils";
import { useQuery, useMutation, gql } from "@apollo/client";

// GraphQL Definitions
const GET_ACCESSORIES = gql`
  query {
    accessories {
      accessoryId
      name
      brand
      category
      price
      description
      image
      quantity
      status
      sellerId
      sellerInfo {
        email
        phoneNumber
      }
      weight
      length
      breadth
      height
    }
  }
`;

const UPDATE_ACCESSORY_STATUS = gql`
  mutation UpdateAccessoryStatus($accessoryId: String!, $status: String!) {
    updateAccessoryStatus(accessoryId: $accessoryId, status: $status) {
      accessoryId
      status
    }
  }
`;

const UPDATE_ACCESSORY = gql`
  mutation UpdateAccessory($accessoryId: String!, $name: String!, $brand: String!, $category: String!, $price: Float!, $quantity: Int!, $description: String!, $weight: Float, $length: Float, $breadth: Float, $height: Float) {
    updateAccessory(accessoryId: $accessoryId, name: $name, brand: $brand, category: $category, price: $price, quantity: $quantity, description: $description, weight: $weight, length: $length, breadth: $breadth, height: $height) {
      accessoryId
      name
      brand
      category
      price
      quantity
      description
      weight
      length
      breadth
      height
    }
  }
`;

const DELETE_ACCESSORY = gql`
  mutation DeleteAccessory($accessoryId: String!) {
    deleteAccessory(accessoryId: $accessoryId) {
      accessoryId
      name
    }
  }
`;

function Accessories() {
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [expandedSeller, setExpandedSeller] = useState(null);
  const [updatingIds, setUpdatingIds] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "sellerId", direction: "asc" });
  const [viewingAccessory, setViewingAccessory] = useState(null);
  const [viewingDescription, setViewingDescription] = useState(null);
  const [editingAccessory, setEditingAccessory] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPage, setProductsPage] = useState({});
  const ITEMS_PER_PAGE = 10;
  const PRODUCTS_PER_PAGE = 10;
  const tableRef = useRef(null);

  // Apollo Hooks
  const { loading, error: queryError, data, refetch } = useQuery(GET_ACCESSORIES, {
    fetchPolicy: "network-only",
  });

  const [updateStatusMutation] = useMutation(UPDATE_ACCESSORY_STATUS);
  const [updateAccessoryMutation] = useMutation(UPDATE_ACCESSORY);
  const [deleteAccessoryMutation] = useMutation(DELETE_ACCESSORY);

  const accessories = data?.accessories || [];
  const error = queryError ? queryError.message : null;

  // Group accessories by seller ID
  const groupedBySeller = accessories.reduce((acc, accessory) => {
    const sellerId = accessory.sellerId;
    if (!acc[sellerId]) {
      acc[sellerId] = {
        sellerId: sellerId,
        sellerInfo: accessory.sellerInfo,
        accessories: []
      };
    }
    acc[sellerId].accessories.push(accessory);
    return acc;
  }, {});

  // Convert to array and sort
  const sellerGroups = Object.values(groupedBySeller).sort((a, b) =>
    a.sellerId.localeCompare(b.sellerId)
  );

  // Calculate seller stats
  const sellerGroupsWithStats = sellerGroups.map(seller => {
    const totalValue = seller.accessories.reduce((sum, a) => sum + (a.price * a.quantity), 0);
    const totalStock = seller.accessories.reduce((sum, a) => sum + a.quantity, 0);
    const approvedAccessories = seller.accessories.filter(a => a.status === 'approved').length;
    const pendingAccessories = seller.accessories.filter(a => a.status === 'pending').length;
    const rejectedAccessories = seller.accessories.filter(a => a.status === 'rejected').length;

    return {
      ...seller,
      totalValue,
      totalStock,
      approvedAccessories,
      pendingAccessories,
      rejectedAccessories,
      totalAccessories: seller.accessories.length
    };
  });

  // Filter sellers based on search
  const filteredSellers = sellerGroupsWithStats.filter(seller => {
    const matchesSearch =
      searchTerm === "" ||
      seller.sellerId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      seller.sellerInfo?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      seller.sellerInfo?.phoneNumber?.includes(searchTerm);

    if (selectedStatus === "all") return matchesSearch;

    // Filter by status
    const filteredAccessories = seller.accessories.filter(accessory =>
      accessory.status.toLowerCase() === selectedStatus
    );

    return matchesSearch && filteredAccessories.length > 0;
  });

  // Sort sellers
  const sortedSellers = [...filteredSellers].sort((a, b) => {
    const key = sortConfig.key;
    let aVal = a[key];
    let bVal = b[key];

    if (key === 'sellerId' || key === 'email') {
      aVal = aVal?.toLowerCase?.() || '';
      bVal = bVal?.toLowerCase?.() || '';
    }

    if (sortConfig.direction === "asc") {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  // Pagination for sellers
  const totalFilteredPages = Math.ceil(sortedSellers.length / ITEMS_PER_PAGE);
  const paginatedSellers = sortedSellers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Get filtered accessories for expanded seller with pagination
  const getFilteredAccessoriesForSeller = (sellerId) => {
    const seller = groupedBySeller[sellerId];
    if (!seller) return [];

    return seller.accessories.filter(accessory => {
      return selectedStatus === "all" || accessory.status.toLowerCase() === selectedStatus;
    });
  };

  // Get paginated products for a seller
  const getPaginatedProductsForSeller = (sellerId) => {
    const filteredAccessories = getFilteredAccessoriesForSeller(sellerId);
    const currentProductsPage = productsPage[sellerId] || 1;
    const startIndex = (currentProductsPage - 1) * PRODUCTS_PER_PAGE;
    const endIndex = startIndex + PRODUCTS_PER_PAGE;

    return {
      products: filteredAccessories.slice(startIndex, endIndex),
      currentPage: currentProductsPage,
      totalPages: Math.ceil(filteredAccessories.length / PRODUCTS_PER_PAGE),
      totalProducts: filteredAccessories.length
    };
  };

  // Handle products page change
  const handleProductsPageChange = (sellerId, page) => {
    setProductsPage(prev => ({
      ...prev,
      [sellerId]: page
    }));
  };

  // Update accessory status
  const updateAccessoryStatus = async (accessoryId, newStatus) => {
    const updatingIdsCopy = new Set(updatingIds);
    updatingIdsCopy.add(`${accessoryId}-${newStatus}`);
    setUpdatingIds(updatingIdsCopy);

    try {
      await updateStatusMutation({
        variables: { accessoryId, status: newStatus.toLowerCase() },
      });
      refetch();
    } catch (err) {
      alert(`Error updating status: ${err.message}`);
    } finally {
      const updatingIdsCopy = new Set(updatingIds);
      updatingIdsCopy.delete(`${accessoryId}-${newStatus}`);
      setUpdatingIds(updatingIdsCopy);
    }
  };

  // Delete accessory
  const deleteAccessory = async (accessoryId) => {
    if (!window.confirm("Are you sure you want to delete this accessory?")) return;

    try {
      await deleteAccessoryMutation({
        variables: { accessoryId },
      });
      refetch();
    } catch (err) {
      alert(`Error deleting accessory: ${err.message}`);
    }
  };

  // Save edited accessory
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingAccessory) return;

    try {
      await updateAccessoryMutation({
        variables: {
          accessoryId: editingAccessory.accessoryId,
          name: editingAccessory.name,
          brand: editingAccessory.brand,
          category: editingAccessory.category || "",
          price: parseFloat(editingAccessory.price),
          quantity: parseInt(editingAccessory.quantity),
          description: editingAccessory.description,
          weight: parseFloat(editingAccessory.weight) || 0,
          length: parseFloat(editingAccessory.length) || 0,
          breadth: parseFloat(editingAccessory.breadth) || 0,
          height: parseFloat(editingAccessory.height) || 0
        },
      });
      setEditingAccessory(null);
      refetch();
    } catch (err) {
      alert(`Error updating accessory: ${err.message}`);
    }
  };

  // View accessory details with actions
  const viewAccessoryDetails = (accessory) => {
    setViewingAccessory(accessory);
  };

  // Close accessory details modal
  const closeAccessoryDetails = () => {
    setViewingAccessory(null);
  };

  // View description only
  const viewDescription = (accessory) => {
    setViewingDescription(accessory);
  };

  // Close description modal
  const closeDescription = () => {
    setViewingDescription(null);
  };

  // Handle edit accessory
  const handleEditAccessory = (accessory) => {
    setEditingAccessory({ ...accessory });
  };

  // Handle seller page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    if (tableRef.current) {
      tableRef.current.scrollLeft = 0;
    }
  };

  const statusStats = {
    all: accessories.length,
    approved: accessories.filter(a => a.status === "approved").length,
    pending: accessories.filter(a => a.status === "pending").length,
    rejected: accessories.filter(a => a.status === "rejected").length,
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return null;
    return <span style={{ marginLeft: '4px', fontSize: '12px' }}>{sortConfig.direction === "asc" ? "↑" : "↓"}</span>;
  };

  const truncateDescription = (description, length = 30) => {
    if (!description) return "No description";
    if (description.length <= length) return description;
    return description.substring(0, length) + "...";
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  // Calculate overall stats
  const totalValue = accessories.reduce((sum, a) => sum + (a.price * a.quantity), 0);
  const totalStock = accessories.reduce((sum, a) => sum + a.quantity, 0);
  const approvedAccessories = accessories.filter(a => a.status === 'approved').length;
  const pendingAccessories = accessories.filter(a => a.status === 'pending').length;

  return (
    <div className="drones-container">
      <div className="main-wrapper">
        {/* Top Navigation */}
        <div className="top-navigation">
          <div className="nav-left">
            <h1>Accessory Management System</h1>
            <p>Admin Dashboard • Manage Seller Accessories • Real-time Updates</p>
          </div>
          <div className="nav-right">
            <button
              className="btn btn-secondary"
              onClick={() => refetch()}
              disabled={loading}
            >
              {loading ? "🔄 Refreshing..." : "🔄 Refresh"}
            </button>
            <button
              className="btn btn-primary"
              onClick={() => {
                const headers = ["Accessory ID", "Seller ID", "Name", "Brand", "Category", "Price", "Quantity", "Status", "Description"];
                const rows = accessories.map(accessory => [
                  accessory.accessoryId,
                  accessory.sellerId,
                  accessory.name,
                  accessory.brand,
                  accessory.category,
                  accessory.price,
                  accessory.quantity,
                  accessory.status,
                  accessory.description
                ]);

                let csvContent = headers.join(',') + '\n';
                rows.forEach(row => {
                  csvContent += row.map(cell => `"${cell}"`).join(',') + '\n';
                });

                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement('a');
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', `accessories_export_${new Date().toISOString().split('T')[0]}.csv`);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
            >
              📊 Export Report
            </button>
          </div>
        </div>

        {/* Compact Stats Dashboard */}
        <div className="compact-stats-container">
          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Total Inventory Value</div>
              <div className="compact-stat-value">₹{totalValue.toLocaleString('en-IN')}</div>

            </div>
          </div>

          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Total Stock Units</div>
              <div className="compact-stat-value">{totalStock}</div>

            </div>
          </div>

          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Total Sellers</div>
              <div className="compact-stat-value">{sellerGroups.length}</div>

            </div>
          </div>

          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Pending Approval</div>
              <div className="compact-stat-value">{pendingAccessories}</div>

            </div>
          </div>
        </div>

        {/* Control Panel */}
        <div className="control-panel">
          <div className="panel-header">
            <h2 className="panel-title">Accessories Management</h2>
          </div>

          <div className="seller-selector">
            <div className="search-container">
              <input
                type="text"
                placeholder="Search sellers by ID, email, or phone..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="search-input"
              />

            </div>

            <div className="status-tabs-container">
              {["all", "approved", "pending", "rejected"].map((status) => (
                <button
                  key={status}
                  className={`status-tab ${selectedStatus === status ? "active" : ""}`}
                  onClick={() => {
                    setSelectedStatus(status);
                    setCurrentPage(1);
                    // Reset product pages when status changes
                    setProductsPage({});
                  }}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                  <span className="tab-count">{statusStats[status]}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <span>{error}</span>
            <button onClick={() => refetch()}>✕</button>
          </div>
        )}

        {/* Main Content Area */}
        <div className="content-area">
          {/* Sellers Table Container */}
          <div className="table-container">
            <div className="table-header">
              <h3 className="table-title">
                All Sellers ({sellerGroups.length})
              </h3>
              <div className="table-controls">
                <div className="table-info">
                  Page {currentPage} of {totalFilteredPages || 1} • Showing {paginatedSellers.length} sellers
                </div>
              </div>
            </div>

            <div className="table-content" ref={tableRef}>
              {loading ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading seller data...</p>
                </div>
              ) : sellerGroups.length === 0 ? (
                <div className="empty-state">
                  <p>No sellers found</p>
                </div>
              ) : (
                <table className="sellers-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort("sellerId")}>
                        Seller ID <SortIcon column="sellerId" />
                      </th>
                      <th onClick={() => handleSort("email")}>
                        Contact Info <SortIcon column="email" />
                      </th>
                      <th onClick={() => handleSort("totalAccessories")}>
                        Accessories <SortIcon column="totalAccessories" />
                      </th>
                      <th onClick={() => handleSort("totalValue")}>
                        Inventory Value <SortIcon column="totalValue" />
                      </th>
                      <th onClick={() => handleSort("totalStock")}>
                        Total Stock <SortIcon column="totalStock" />
                      </th>
                      <th>Status Breakdown</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedSellers.map((seller) => {
                      const { products, currentPage: productsCurrentPage, totalPages: productsTotalPages, totalProducts } = getPaginatedProductsForSeller(seller.sellerId);

                      return (
                        <React.Fragment key={seller.sellerId}>
                          <tr
                            className={`seller-row ${expandedSeller === seller.sellerId ? 'expanded' : ''}`}
                            onClick={() => setExpandedSeller(
                              expandedSeller === seller.sellerId ? null : seller.sellerId
                            )}
                            style={{ cursor: 'pointer' }}
                          >
                            <td>
                              <div className="seller-id-cell">
                                <span className="expand-icon">
                                  {expandedSeller === seller.sellerId ? '▼' : '▶'}
                                </span>
                                <strong>Seller #{seller.sellerId}</strong>
                              </div>
                            </td>
                            <td>
                              <div className="seller-contact-cell">
                                <div className="seller-email">
                                  📧 {seller.sellerInfo?.email || "No email"}
                                </div>
                                <div className="seller-phone">
                                  📱 {seller.sellerInfo?.phoneNumber || "No phone"}
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="seller-products-count">
                                <span className="count-badge">{seller.totalAccessories}</span>
                                <span className="count-label">accessories</span>
                              </div>
                            </td>
                            <td>
                              <div className="seller-value">
                                <strong className="value-amount">
                                  ₹{seller.totalValue.toLocaleString('en-IN')}
                                </strong>
                              </div>
                            </td>
                            <td>
                              <div className="seller-stock">
                                <span className="stock-count">{seller.totalStock}</span>
                                <span className="stock-label">units</span>
                              </div>
                            </td>
                            <td>
                              <div className="status-breakdown">
                                <span className="status-dot approved" title={`${seller.approvedAccessories} approved`}>
                                  {seller.approvedAccessories}
                                </span>
                                <span className="status-dot pending" title={`${seller.pendingAccessories} pending`}>
                                  {seller.pendingAccessories}
                                </span>
                                <span className="status-dot rejected" title={`${seller.rejectedAccessories} rejected`}>
                                  {seller.rejectedAccessories}
                                </span>
                              </div>
                            </td>
                            <td>
                              <button
                                className="action-btn action-view"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedSeller(
                                    expandedSeller === seller.sellerId ? null : seller.sellerId
                                  );
                                }}
                                title="View Accessories"
                              >
                                {expandedSeller === seller.sellerId ? '👁️‍🗨️' : '👁️'}
                              </button>
                            </td>
                          </tr>

                          {/* Expanded Accessories Row */}
                          {expandedSeller === seller.sellerId && (
                            <tr className="products-row">
                              <td colSpan="7">
                                <div className="products-container">
                                  <div className="products-header">
                                    <div>
                                      <h4>Accessories from Seller #{seller.sellerId}</h4>
                                      <span className="products-count">
                                        {totalProducts} accessories • Showing {products.length} on page {productsCurrentPage} of {productsTotalPages}
                                      </span>
                                    </div>
                                  </div>

                                  {totalProducts === 0 ? (
                                    <div className="no-products">
                                      <p>No accessories found for the selected status filter.</p>
                                    </div>
                                  ) : (
                                    <>
                                      <div className="products-table-container">
                                        <table className="products-table">
                                          <thead>
                                            <tr>
                                              <th>Accessory ID</th>
                                              <th>Accessory Details</th>
                                              <th>Brand</th>
                                              <th>Category</th>
                                              <th>Price</th>
                                              <th>Stock</th>
                                              <th>Status</th>
                                              <th>Description</th>
                                              <th>Actions</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {products.map((accessory) => (
                                              <tr key={accessory.accessoryId}>
                                                <td>
                                                  <div className="id-cell">
                                                    {accessory.accessoryId}
                                                  </div>
                                                </td>
                                                <td>
                                                  <div className="drone-details-cell">
                                                    {accessory.image && (
                                                      <img src={accessory.image} alt={accessory.name} className="drone-image" />
                                                    )}
                                                    <div>
                                                      <div className="drone-name">{accessory.name}</div>
                                                      <div className="drone-specs">
                                                        <span className="drone-brand">{accessory.brand}</span>
                                                      </div>
                                                    </div>
                                                  </div>
                                                </td>
                                                <td>
                                                  <div className="drone-brand">{accessory.brand}</div>
                                                </td>
                                                <td>
                                                  <div className="drone-uin">{accessory.category || "N/A"}</div>
                                                </td>
                                                <td>
                                                  <div className="price-cell">
                                                    {formatPrice(accessory.price)}
                                                  </div>
                                                </td>
                                                <td>
                                                  <div className={`quantity-cell ${accessory.quantity === 0 ? 'out-of-stock' : ''}`}>
                                                    {accessory.quantity}
                                                    {accessory.quantity < 10 && accessory.quantity > 0 && (
                                                      <span className="low-stock-indicator">!</span>
                                                    )}
                                                  </div>
                                                </td>
                                                <td>
                                                  <span className={`status-badge status-${accessory.status}`}>
                                                    {accessory.status.toUpperCase()}
                                                  </span>
                                                </td>
                                                <td>
                                                  <div>
                                                    {truncateDescription(accessory.description, 30)}
                                                    {accessory.description && accessory.description.length > 30 && (
                                                      <button
                                                        onClick={() => viewDescription(accessory)}
                                                        className="read-more-btn"
                                                      >
                                                        Read more
                                                      </button>
                                                    )}
                                                  </div>
                                                </td>
                                                <td>
                                                  <div className="action-buttons">
                                                    {accessory.status === "pending" && (
                                                      <>
                                                        <button
                                                          className="action-btn action-approve"
                                                          onClick={() => updateAccessoryStatus(accessory.accessoryId, "approved")}
                                                          disabled={updatingIds.has(`${accessory.accessoryId}-approved`)}
                                                          title="Approve"
                                                        >
                                                          {updatingIds.has(`${accessory.accessoryId}-approved`) ? "..." : "✓"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-reject"
                                                          onClick={() => updateAccessoryStatus(accessory.accessoryId, "rejected")}
                                                          disabled={updatingIds.has(`${accessory.accessoryId}-rejected`)}
                                                          title="Reject"
                                                        >
                                                          {updatingIds.has(`${accessory.accessoryId}-rejected`) ? "..." : "✗"}
                                                        </button>
                                                      </>
                                                    )}

                                                    {accessory.status === "approved" && (
                                                      <>
                                                        <button
                                                          className="action-btn action-pending"
                                                          onClick={() => updateAccessoryStatus(accessory.accessoryId, "pending")}
                                                          disabled={updatingIds.has(`${accessory.accessoryId}-pending`)}
                                                          title="Move to Pending"
                                                        >
                                                          {updatingIds.has(`${accessory.accessoryId}-pending`) ? "..." : "⏳"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-reject"
                                                          onClick={() => updateAccessoryStatus(accessory.accessoryId, "rejected")}
                                                          disabled={updatingIds.has(`${accessory.accessoryId}-rejected`)}
                                                          title="Reject"
                                                        >
                                                          {updatingIds.has(`${accessory.accessoryId}-rejected`) ? "..." : "✗"}
                                                        </button>
                                                      </>
                                                    )}

                                                    {accessory.status === "rejected" && (
                                                      <>
                                                        <button
                                                          className="action-btn action-pending"
                                                          onClick={() => updateAccessoryStatus(accessory.accessoryId, "pending")}
                                                          disabled={updatingIds.has(`${accessory.accessoryId}-pending`)}
                                                          title="Move to Pending"
                                                        >
                                                          {updatingIds.has(`${accessory.accessoryId}-pending`) ? "..." : "⏳"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-approve"
                                                          onClick={() => updateAccessoryStatus(accessory.accessoryId, "approved")}
                                                          disabled={updatingIds.has(`${accessory.accessoryId}-approved`)}
                                                          title="Approve"
                                                        >
                                                          {updatingIds.has(`${accessory.accessoryId}-approved`) ? "..." : "✓"}
                                                        </button>
                                                      </>
                                                    )}

                                                    <button
                                                      className="action-btn action-view"
                                                      onClick={() => viewAccessoryDetails(accessory)}
                                                      title="View Details"
                                                    >
                                                      👁
                                                    </button>



                                                    <button
                                                      className="action-btn action-delete"
                                                      onClick={() => deleteAccessory(accessory.accessoryId)}
                                                      title="Delete"
                                                    >
                                                      🗑
                                                    </button>
                                                  </div>
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>

                                      {/* Products Pagination */}
                                      {productsTotalPages > 1 && (
                                        <div className="products-pagination">
                                          <button
                                            onClick={() => handleProductsPageChange(seller.sellerId, productsCurrentPage - 1)}
                                            disabled={productsCurrentPage === 1}
                                            className="pagination-btn"
                                          >
                                            &lt; Previous
                                          </button>
                                          <div className="pagination-info">
                                            Page {productsCurrentPage} of {productsTotalPages}
                                          </div>
                                          <button
                                            onClick={() => handleProductsPageChange(seller.sellerId, productsCurrentPage + 1)}
                                            disabled={productsCurrentPage === productsTotalPages}
                                            className="pagination-btn"
                                          >
                                            Next &gt;
                                          </button>
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination Controls for Sellers */}
            {totalFilteredPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  &lt;
                </button>
                {Array.from({ length: totalFilteredPages }, (_, i) => i + 1).map((num) => (
                  <button
                    key={num}
                    className={currentPage === num ? "active" : ""}
                    onClick={() => handlePageChange(num)}
                  >
                    {num}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalFilteredPages}
                >
                  &gt;
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Accessory Details Modal with Actions */}
      {viewingAccessory && (
        <div className="modal-overlay" onClick={closeAccessoryDetails}>
          <div className="modal-content drone-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Accessory Details</h2>
              <button className="close-btn" onClick={closeAccessoryDetails}>×</button>
            </div>
            <div className="modal-body">
              <div className="drone-details-grid">
                <div className="drone-image-section">
                  {viewingAccessory.image ? (
                    <img
                      src={viewingAccessory.image}
                      alt={viewingAccessory.name}
                      className="drone-detail-image"
                    />
                  ) : (
                    <div className="no-image-placeholder">No Image</div>
                  )}
                </div>

                <div className="drone-info-section">
                  <div className="info-row">
                    <div className="info-label">Accessory ID</div>
                    <div className="info-value">{viewingAccessory.accessoryId}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Name</div>
                    <div className="info-value">{viewingAccessory.name}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Brand</div>
                    <div className="info-value">{viewingAccessory.brand}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Category</div>
                    <div className="info-value">{viewingAccessory.category || "N/A"}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Price</div>
                    <div className="info-value price-value">{formatPrice(viewingAccessory.price)}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Dimensions</div>
                    <div className="info-value">
                      {viewingAccessory.length || 0} x {viewingAccessory.breadth || 0} x {viewingAccessory.height || 0} cm
                    </div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Weight</div>
                    <div className="info-value">{viewingAccessory.weight || 0} kg</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Quantity</div>
                    <div className="info-value">
                      <span className={`quantity-display ${viewingAccessory.quantity === 0 ? 'out-of-stock' : ''}`}>
                        {viewingAccessory.quantity}
                      </span>
                    </div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Status</div>
                    <div className="info-value">
                      <span className={`status-badge status-${viewingAccessory.status}`}>
                        {viewingAccessory.status.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Seller ID</div>
                    <div className="info-value">{viewingAccessory.sellerId}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Seller Email</div>
                    <div className="info-value">{viewingAccessory.sellerInfo?.email || "N/A"}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Seller Phone</div>
                    <div className="info-value">{viewingAccessory.sellerInfo?.phoneNumber || "N/A"}</div>
                  </div>
                </div>

                <div className="drone-description-section">
                  <div className="info-label">Description</div>
                  <div className="description-content">
                    {viewingAccessory.description || 'No description available'}
                  </div>
                </div>

                {/* Actions in Details Modal */}
                <div className="drone-actions-section">
                  <div className="info-label">Quick Actions</div>
                  <div className="action-buttons-grid">
                    {viewingAccessory.status === "pending" && (
                      <>
                        <button
                          className="action-btn-large action-approve"
                          onClick={() => {
                            updateAccessoryStatus(viewingAccessory.accessoryId, "approved");
                            closeAccessoryDetails();
                          }}
                          disabled={updatingIds.has(`${viewingAccessory.accessoryId}-approved`)}
                          title="Approve"
                        >
                          {updatingIds.has(`${viewingAccessory.accessoryId}-approved`) ? "..." : "✓ Approve"}
                        </button>
                        <button
                          className="action-btn-large action-reject"
                          onClick={() => {
                            updateAccessoryStatus(viewingAccessory.accessoryId, "rejected");
                            closeAccessoryDetails();
                          }}
                          disabled={updatingIds.has(`${viewingAccessory.accessoryId}-rejected`)}
                          title="Reject"
                        >
                          {updatingIds.has(`${viewingAccessory.accessoryId}-rejected`) ? "..." : "✗ Reject"}
                        </button>
                      </>
                    )}

                    {viewingAccessory.status === "approved" && (
                      <>
                        <button
                          className="action-btn-large action-pending"
                          onClick={() => {
                            updateAccessoryStatus(viewingAccessory.accessoryId, "pending");
                            closeAccessoryDetails();
                          }}
                          disabled={updatingIds.has(`${viewingAccessory.accessoryId}-pending`)}
                          title="Move to Pending"
                        >
                          {updatingIds.has(`${viewingAccessory.accessoryId}-pending`) ? "..." : "⏳ To Pending"}
                        </button>
                        <button
                          className="action-btn-large action-reject"
                          onClick={() => {
                            updateAccessoryStatus(viewingAccessory.accessoryId, "rejected");
                            closeAccessoryDetails();
                          }}
                          disabled={updatingIds.has(`${viewingAccessory.accessoryId}-rejected`)}
                          title="Reject"
                        >
                          {updatingIds.has(`${viewingAccessory.accessoryId}-rejected`) ? "..." : "✗ Reject"}
                        </button>
                      </>
                    )}

                    {viewingAccessory.status === "rejected" && (
                      <>
                        <button
                          className="action-btn-large action-pending"
                          onClick={() => {
                            updateAccessoryStatus(viewingAccessory.accessoryId, "pending");
                            closeAccessoryDetails();
                          }}
                          disabled={updatingIds.has(`${viewingAccessory.accessoryId}-pending`)}
                          title="Move to Pending"
                        >
                          {updatingIds.has(`${viewingAccessory.accessoryId}-pending`) ? "..." : "⏳ To Pending"}
                        </button>
                        <button
                          className="action-btn-large action-approve"
                          onClick={() => {
                            updateAccessoryStatus(viewingAccessory.accessoryId, "approved");
                            closeAccessoryDetails();
                          }}
                          disabled={updatingIds.has(`${viewingAccessory.accessoryId}-approved`)}
                          title="Approve"
                        >
                          {updatingIds.has(`${viewingAccessory.accessoryId}-approved`) ? "..." : "✓ Approve"}
                        </button>
                      </>
                    )}



                    <button
                      className="action-btn-large action-delete"
                      onClick={() => {
                        if (window.confirm("Are you sure you want to delete this accessory?")) {
                          deleteAccessory(viewingAccessory.accessoryId);
                          closeAccessoryDetails();
                        }
                      }}
                      title="Delete"
                    >
                      🗑 Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Description Modal */}
      {viewingDescription && (
        <div className="modal-overlay" onClick={closeDescription}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Description</h2>
              <button className="close-btn" onClick={closeDescription}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ lineHeight: '1.6', margin: 0, whiteSpace: 'pre-wrap' }}>
                {viewingDescription.description}
              </p>
            </div>
          </div>
        </div>
      )}


    </div>
  );
}

export default Accessories;